import * as React from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import {Alert, Box} from '@mui/material'
import {useSnackbar} from 'notistack'
import withLayout from '../../../hocs/Layout'
import PageTitle from '../../../common/PageTitle'
import {pageContainerSx, sectionCardSx} from '../../../styles/patterns'
import {SIZE} from '../../../styles/foundation/typography'
import {GAP} from '../../../styles/foundation/spacing'
import {
  SECTIONS, MODULES, resolveSection, moduleForSection,
  moduleForPath, pathForModule, leafForModule,
} from './sections'
import {NOW, APPROVAL_NOTICE} from './data/reference'
import {pinClock, unpinClock} from './data/clock'
import {
  fetchAllRequests, apiRaiseRequest, apiUploadEvidence, apiSubmitAdditionalData, apiAcceptProposal, apiDeclineProposal,
  apiCatalogue, apiSubcategories, apiPacks,
} from './data/api'
import {mapRequestFromApi, toRaiseBody} from './data/mapRequest'
import {mapCataloguePage, mapSubcategories, mapPacks} from './data/mapCatalogue'
import {setVolumetricDivisor} from './weightMath'
import StepRail from './components/StepRail'
import RqKpiStrip from './components/RqKpiStrip'
import EmptyState from './components/EmptyState'
import SopPage from './SopPage'
import CataloguePage, {catalogueStatCards} from './catalogue/CataloguePage'
import PackingPage, {packingStatCards} from './packing/PackingPage'
import DwsPage from './DwsPage'
import FreezeDashboard from './initiate/FreezeDashboard'
import {useFreezeStore} from './state/useFreezeStore'

// Fallback for a leaf that names no component. Every leaf in SECTIONS maps to
// one today, so this is unreachable — it stays as the guard for the next leaf
// somebody registers in sections.js before its page exists, which is exactly
// how the previous six sections were built.
function SectionPlaceholder({section}) {
  return (
    <Box sx={sectionCardSx}>
      <EmptyState icon={section.icon} title={section.label} body="This section hasn't been built yet." />
    </Box>
  )
}

// Leaf id -> component. Two leaves share a component with a discriminator
// prop (see sectionExtras below): `subcat` and `packing` are the two halves of
// PackingPage, split so each of the sub-module's steps gets its own tab.
// The page renders at 80% of its natural size, which is what brings it level
// with Weight Discrepancy. The reasoning for expressing that as `zoom` rather
// than as hand-edited values lives in disputes-v2/index.js:22-42 and is not
// repeated here. Two consequences matter when reading the container below:
//
// - One declaration scales type, padding, borders and column widths together,
//   so the sizes authored throughout this module stay as written.
// - Overlays (drawers, dialogs, popovers, Select menus) render in portals
//   attached to document.body, OUTSIDE this container, so they are NOT scaled.
//   That is deliberate, not an oversight.
const PAGE_ZOOM = 0.8

const SECTION_COMPONENTS = {
  sop: SopPage,
  catalogue: CataloguePage,
  subcat: PackingPage,
  packing: PackingPage,
  dws: DwsPage,
  freeze: FreezeDashboard,
}

// Leaf id -> the stat cards that head it, or absent for a leaf whose page is
// its own header. Only Package master's three leaves have any: their figures
// describe the leaf, not the sub-module, so unlike Weight Freeze's five
// request counts the strip has to change with the tab. That is the point of it
// — the numbers say what you are looking at — and it is why the cards are
// built here rather than passed in once.
//
// Each entry is a builder EXPORTED BY THE LEAF'S OWN PAGE, so the derivation
// stays where it already lived (catalogueMath's counters, packingModel's
// subcategoryRows/observedBaskets) and this container never learns any leaf's
// arithmetic.
const LEAF_STATS = {
  catalogue: (store) => catalogueStatCards(store.skus),
  subcat: (store) => packingStatCards(store, 'subcat'),
  packing: (store) => packingStatCards(store, 'packing'),
}

// Two axes, two owners: the MODULE axis (which sub-module) is
// controlled from outside when `moduleId` is supplied, and the LEAF axis (which
// section inside it) is always this component's own state.
//
// `moduleId` + `onModuleChange` are optional, and with both absent this behaves
// exactly as it did before the sub-modules became URLs: the module follows the
// leaf and the level-1 strip below is the only thing that selects one. That
// uncontrolled mode is what the dev harness mounts — see the routed wrapper at
/** Leaves still rendering the frozen sample dataset in live mode. */
const SAMPLE_LEAVES = ['dws']

// the bottom of this file for why the router hooks are not in here.
function WeightFreezeV2({moduleId, onModuleChange, live = false}) {
  // The module clock follows the mode (data/clock.js): the wall clock on a live mount, the
  // dataset's pinned instant when seeded. Set synchronously, before anything below reads "today".
  if (live) unpinClock(); else pinClock(NOW)
  const {enqueueSnackbar} = useSnackbar()

  // `active` is the LEAF id, not the module — the module is derived from it.
  // Storing the leaf is what lets the dozen existing `onNavigate('dws')`-style
  // cross-links keep working untouched through a two-level nav: they name a
  // leaf, and the strip above simply follows along to whichever module owns
  // it. Storing the module instead would have meant editing every one of
  // those call sites to say which module their target now lives in.
  const [active, setActive] = React.useState('sop')

  // Which view the Initiate-freeze dashboard opens on. Held here rather than
  // inside the dashboard because it is set by navigation: a link that used to
  // say `onNavigate('requests')` meant "show them the statuses", and that
  // intent has to survive the two ids collapsing into one leaf.
  const [freezeView, setFreezeView] = React.useState('initiate')

  const railRef = React.useRef(null)
  const headingRef = React.useRef(null)

  // The handle a dozen drawers across this module reach for when closing them
  // leaves focus on <body>: they call `focusFallbackRef.current.focusActive()`
  // and expect focus to land on the level-1 strip. The strip is no longer always
  // there — in the app the sidebar owns the module axis, so it is unmounted, and
  // on the Initiate-freeze module the level-2 strip is suppressed too, which
  // would leave those closes with nothing at all to focus. Every call site
  // null-guards, so nothing would throw; focus would just be silently dropped,
  // which is the kind of keyboard regression nobody notices until they cannot
  // tab out of a closed drawer.
  //
  // Shaped like a ref (`{current: {focusActive()}}`) so no consumer changes, and
  // memoised so it is one stable object for the life of the mount rather than a
  // new prop identity on every render.
  const focusFallback = React.useMemo(() => ({
    current: {
      focusActive() {
        if (railRef.current) railRef.current.focusActive()
        else if (headingRef.current) headingRef.current.focus()
      },
    },
  }), [])

  // Held ABOVE the section switch below (deliberately not inside either
  // branch) so it survives every rail click. See state/useFreezeStore.js's
  // own header for why: each of the six sections used to keep its own
  // section-local state layered on the frozen dataset import, which meant
  // switching rail items unmounted whichever section was active and threw
  // away everything the merchant had just done in it. One store,
  // instantiated once here and threaded down, is what makes state survive
  // navigation instead.
  const store = useFreezeStore({live})

  // LIVE (the real route): the request list comes from the backend once, mapped into the panel's
  // own shape (data/mapRequest.js); every write below goes to the API and the store only ever
  // holds what the server returned. Seeded mounts (harness, tests) never reach any of this.
  const storeActions = store.actions
  React.useEffect(() => {
    if (!live) return undefined
    let cancelled = false
    fetchAllRequests()
      .then((rows) => { if (!cancelled) storeActions.setRequests(rows.map((r) => mapRequestFromApi(r)).filter(Boolean)) })
      .catch((err) => {
        if (!cancelled) enqueueSnackbar(`Could not load your weight freeze requests: ${(err && err.message) || 'network error'}`, {variant: 'error'})
      })
    return () => { cancelled = true }
  }, [live, storeActions, enqueueSnackbar])

  // The catalogue, sub-categories and packing materials, from the merchant's own masters. Fetched
  // together because the three sections show one thing three ways and must agree on screen; failing
  // separately would let the catalogue count disagree with the sub-category count in front of the
  // merchant. One failure is reported once, and the sections render their empty state.
  React.useEffect(() => {
    if (!live) return undefined
    let cancelled = false
    Promise.all([apiCatalogue({limit: 500}), apiSubcategories(), apiPacks()])
      .then(([cat, subs, packs]) => {
        if (cancelled) return
        setVolumetricDivisor(cat && cat.volumetricDivisor)
        storeActions.setCatalogue(mapCataloguePage(cat).rows)
        storeActions.setSubcats(mapSubcategories(subs))
        storeActions.setPacks(mapPacks(packs))
      })
      .catch((err) => {
        if (!cancelled) enqueueSnackbar(`Could not load your catalogue: ${(err && err.message) || 'network error'}`, {variant: 'error'})
      })
    return () => { cancelled = true }
  }, [live, storeActions, enqueueSnackbar])

  // The current list, readable from inside the memoised actions without re-creating them.
  const requestsRef = React.useRef(store.requests)
  requestsRef.current = store.requests

  const liveActions = React.useMemo(() => {
    if (!live) return null
    const backendIdOf = (id) => {
      const hit = (requestsRef.current || []).find((r) => r.id === id || r.backendId === id)
      return hit ? hit.backendId : id
    }
    const fail = (err, fallback) => enqueueSnackbar((err && err.message) || fallback, {variant: 'error'})
    return {
      ...storeActions,
      // Raise: JSON first (the server mints the reference and applies its rules), then the
      // merchant's own video/stills as multipart. Resolves to the mapped request; a refusal
      // (the category rule, a missing weight) is thrown for the caller to show.
      raiseRequest: async (payload, extra) => {
        const saved = await apiRaiseRequest(toRaiseBody(payload, extra))
        let full = saved
        if (payload && payload.evidenceRaw) {
          try {
            const withFiles = await apiUploadEvidence(saved.id, payload.evidenceRaw)
            if (withFiles) full = withFiles
          } catch (err) {
            fail(err, 'The request was raised, but the evidence upload failed — add it again from Requests & statuses.')
          }
        }
        const mapped = mapRequestFromApi(full)
        storeActions.addRequest(mapped)
        return mapped
      },
      submitAdditionalData: async (id, payload) => {
        try {
          const updated = await apiSubmitAdditionalData(backendIdOf(id), payload)
          const mapped = mapRequestFromApi(updated)
          storeActions.replaceRequest(mapped)
          enqueueSnackbar(`Submission ${mapped.submissions} of 3 sent. ${mapped.id} is now on round ${mapped.round}, awaiting a Prozo decision.`, {variant: 'success'})
        } catch (err) {
          fail(err, 'Could not send the additional data')
        }
      },
      acceptProposal: async (id) => {
        try {
          const mapped = mapRequestFromApi(await apiAcceptProposal(backendIdOf(id)))
          storeActions.replaceRequest(mapped)
          enqueueSnackbar(`${mapped.id}: ${APPROVAL_NOTICE}`, {variant: 'success'})
        } catch (err) {
          fail(err, 'Could not accept the proposal')
        }
      },
      declineProposal: async (id, remark) => {
        try {
          const mapped = mapRequestFromApi(await apiDeclineProposal(backendIdOf(id), remark))
          storeActions.replaceRequest(mapped)
          enqueueSnackbar(`${mapped.id}: proposal declined.`, {variant: 'info'})
        } catch (err) {
          fail(err, 'Could not decline the proposal')
        }
      },
    }
  }, [live, storeActions, enqueueSnackbar])

  // What the sections receive: the store as-is when seeded; with the API-backed actions when live.
  const exposed = live ? {...store, actions: liveActions, live: true} : store

  // The controlled module wins when supplied; otherwise the module follows the
  // remembered leaf, which is the pre-URL behaviour the harness still relies on.
  // Resolved BEFORE `navigate` below rather than beside the other derived values
  // further down, because that callback's dependency array reads it during
  // render — a later `const` would be a temporal-dead-zone throw, not a stale
  // closure.
  const activeModule = moduleId
    ? MODULES.find((m) => m.id === moduleId) || MODULES[0]
    : moduleForSection(active)

  // The single entry point for every navigation in the module — the rail, the
  // leaf tabs and every in-page cross-link all come through here, so alias
  // resolution can't be forgotten at one of the three.
  const navigate = React.useCallback((id) => {
    // Only the two legacy ids carry an opinion about which view to open. A
    // plain `navigate('freeze')` — the rail tab, or a return visit — leaves the
    // dashboard on whichever view it was last showing, exactly as the other
    // leaves keep their own state.
    if (id === 'requests') setFreezeView('status')
    if (id === 'initiate') setFreezeView('initiate')
    const leaf = resolveSection(id)
    setActive(leaf)

    // A cross-link naming a leaf of ANOTHER sub-module has to move the module
    // axis as well, and when that axis is owned outside (the app, where it is
    // the URL) this component cannot move it itself — it reports the new owner
    // and lets the owner navigate. Links inside the current module skip the
    // call and stay a pure state change, so they cause no URL churn.
    const owner = moduleForSection(leaf)
    if (onModuleChange && owner.id !== activeModule.id) onModuleChange(owner.id)
  }, [onModuleChange, activeModule.id])

  // DERIVED, deliberately not synced with an effect. `active` is the leaf the
  // merchant last chose; after a sidebar click it still names a leaf of the
  // module they just left, so the rendered leaf has to be clamped to the module
  // on screen. An effect that corrected `active` instead would paint one frame
  // of the wrong section and would fight the URL on browser back/forward.
  //
  // It also buys per-module leaf memory for free: because a sidebar click never
  // calls setActive, leaving Package master on Packing material and coming back
  // later re-selects Packing material rather than resetting to Catalogue.
  const activeLeaf = leafForModule(activeModule.id, active)

  const moduleSections = activeModule.sections.map((id) => SECTIONS.find((s) => s.id === id))
  const activeSection = SECTIONS.find((s) => s.id === activeLeaf) || SECTIONS[0]
  const ActiveComponent = SECTION_COMPONENTS[activeLeaf]

  // Built from the CLAMPED leaf, never from `active`: after a sidebar click the
  // remembered leaf can still name a section of the module being left, and a
  // strip built from that would head Package master with the figures of a page
  // that is not on screen. Recomputed per render rather than memoised — the
  // store is a fresh object each render, so a `useMemo` keyed on it would
  // recompute anyway while pretending not to, and each builder is the same work
  // the leaf pages did in their own render bodies before the row moved up here.
  const statCards = LEAF_STATS[activeLeaf] ? LEAF_STATS[activeLeaf](store) : null

  // The props that only one or two leaves take. Kept in one place rather than
  // as conditionals in the JSX so the spread below stays a single expression.
  const sectionExtras =
    activeLeaf === 'subcat' ? {part: 'subcat'}
      : activeLeaf === 'packing' ? {part: 'packing'}
        : activeLeaf === 'freeze' ? {initialView: freezeView}
          : {}

  return (
    <Box sx={{
      ...pageContainerSx,
      zoom: PAGE_ZOOM,

      // The four rules below sit here rather than at the ~60 call sites they
      // cover, because a container-class descendant selector is specificity
      // (0,2,0): it beats both the `sx` on the element itself (0,1,0) and
      // MUI's theme defaults. Same cascade DisputeGrid.js:154-168 uses.

      // Filter and Select text. Fourteen sites already ask for SIZE['2xs'] on
      // their FormControl/TextField, but MUI's InputBase root spreads body1
      // (16px — theme/typography.js:74-77) straight onto .MuiInputBase-input,
      // which wins over inheritance, so those controls render 16px today. This
      // is the fix for that bug, not a size preference.
      '& .MuiOutlinedInput-input, & .MuiSelect-select': {fontSize: SIZE['2xs']},

      // headerCellSx is SIZE.xs for the whole app; disputes steps the shared
      // pattern down at its own call site (DisputeGrid.js:42) and so do we.
      '& .MuiTableCell-head': {fontSize: SIZE['2xs']},

      // A DEFAULT rather than an override: a cell that nests a Typography with
      // its own fontSize still wins on itself, which is intended. What this
      // catches is the bare-value cells — a table that prints raw numbers with
      // no Typography would otherwise take cellSx's SIZE.base and render a full
      // size larger than the identity cell beside it.
      // Padding matches disputes' py: 0.75 and CELL_PX (gridSx.js:31).
      '& .MuiTableCell-body': {fontSize: SIZE['2xs'], py: 0.75, px: 1},
    }}>
      {/* The focus anchor for `focusFallback` above, and the reason it is a
          wrapper rather than a ref on the title: PageTitle renders null — it
          publishes the title into the shell's own header context — so there is
          no element of ours to point at. An empty, zero-height, programmatically
          focusable box at the top of the module is a valid focus target and puts
          the next Tab press at the start of the page's content. */}
      {/* The header names the SUB-MODULE, not the module. A merchant arrives on
          one screen at a time, and the sidebar row they clicked already said
          "Weight Freeze"; repeating it while the actual screen went unnamed made
          the header the one place in the app that could not tell you where you
          were. `blurb` (sections.js) is the matching one-liner — the paragraph
          that used to sit here was identical on all five screens and long enough
          that the header clipped it mid-sentence. */}
      <Box ref={headingRef} tabIndex={-1} sx={{outline: 'none'}}>
        <PageTitle subTitle={activeModule.blurb}>{activeModule.label}</PageTitle>
      </Box>

      {/* Level 1: the sub-modules — but ONLY when nobody outside owns that
          axis. In the app each sub-module is its own URL, reached from a row in
          the sidebar or a button on a sibling screen, so this strip would be a
          second control for an axis the app's global navigation already drives,
          and the two can genuinely disagree (browser Back moves one of them).
          The app removed the standalone sidebar entries when create-order became
          tabs of one page; this is the same trade in the other direction. What
          is left renders it: the dev harness, which has no sidebar to switch
          sub-modules with.

          Clicking a tab lands on that module's first leaf, and deliberately does
          not touch `freezeView` — the dashboard keeps whichever view it was left
          on, the same way the other leaves keep their own state. */}
      {!moduleId && (
        <Box sx={{mb: 1.5}}>
          <StepRail
            ref={railRef}
            label='Merchant Weight Freeze sub-modules'
            sections={MODULES}
            active={activeModule.id}
            onChange={(id) => navigate((MODULES.find((m) => m.id === id) || MODULES[0]).sections[0])}
          />
        </Box>
      )}

      {/* Level 2: the sub-module's own header — its figures, then the leaves
          inside it. Suppressed entirely for a single-leaf module (Weight Freeze
          draws its own header; DWS data, Overview and Analytics have no leaf
          axis), where a one-item tab strip would carry no information and cost
          a row.

          Strip and row are ONE block, with the same treatment and the same
          internal spacing the Freeze-weight dashboard uses — same components,
          same density, same GAP.md between them — because it is the same header
          doing the same job one level down. The strip is above the row rather
          than beside it: it is the context the row's tabs are selected within.

          Unlike Weight Freeze's, these figures are per-leaf, so the strip
          changes as the tabs do. See LEAF_STATS above. */}
      {moduleSections.length > 1 && (
        <Box sx={{display: 'grid', gap: GAP.md, mb: 2.5}}>
          {statCards && (
            <RqKpiStrip cards={statCards} label={`${activeModule.label} figures`} />
          )}
          <StepRail
            sections={moduleSections}
            active={activeLeaf}
            onChange={navigate}
            density='comfortable'
            label={`${activeModule.label} sections`}
            // Three tabs scroll fine on a phone, and collapsing them to a
            // select here would make the sub-module's own header a different
            // shape from the Freeze-weight header it is matching.
            disableSelectFallback
          />
        </Box>
      )}

      {/* Staged release, said on screen. Requests, Raise, Catalogue, Sub-categories and Packing now
          read the merchant's own data; DWS evidence is the one leaf still on the sample dataset,
          because the sorter readings it shows have no read model yet. */}
      {live && SAMPLE_LEAVES.includes(activeLeaf) && (
        <Alert severity='info' sx={{mb: 1.5, fontSize: SIZE['2xs'], '& .MuiAlert-message': {lineHeight: 1.6}}}>
          <Box component='span' sx={{fontWeight: 700}}>Sample data — this section is not live yet.</Box>{' '}
          Requests, Raise, Catalogue, Sub-categories and Packing material show your own data. DWS evidence connects next.
        </Alert>
      )}

      <Box sx={{width: '100%'}}>
        {ActiveComponent ? (
          // Spread the whole store rather than an explicit per-field list:
          // SopPage only ever read skus/requests/subcats/packs, but
          // Catalogue's bridge (see state/useFreezeStore.js) also needs
          // mapLog/catalogueSyncing/actor/actions, and the next section to
          // register here will need its own subset again. A named prop
          // list would need editing on every new section's requirements;
          // the spread does not, and every section still only reads the
          // handful of keys it destructures.
          //
          // `focusFallbackRef` is passed alongside the spread (not part of
          // the store) so a section's own drawer can recover keyboard
          // focus onto the active strip tab if closing it leaves nothing
          // else to focus — see CataloguePage's merge drawer for the one
          // section that needs this today.
          //
          // `key` remounts the two PackingPage halves across a subcat<->packing
          // switch. Without it React reconciles them as the same element and
          // the panels of the outgoing half stay mounted with their drawers
          // open, since only the `part` prop changed.
          <ActiveComponent key={activeLeaf} onNavigate={navigate} focusFallbackRef={focusFallback} {...exposed} {...sectionExtras} />
        ) : (
          <SectionPlaceholder section={activeSection} />
        )}
      </Box>
    </Box>
  )
}

/**
 * The app-side entry point: the URL owns which sub-module is on screen.
 *
 * WHY THE ROUTER HOOKS ARE HERE AND NOT IN THE PAGE COMPONENT ABOVE.
 * WeightFreezeV2 is mounted from two places — this wrapper, on the three
 * `/weight-freeze-v2` routes, and the dev harness at `/__weight-harness` (see
 * __harness__/WeightHarness.js), which is not one of this module's routes at
 * all. If `useLocation`/`useNavigate` lived inside the page, the harness would
 * read its own path, resolve it to the first sub-module and be permanently
 * stuck there, and any cross-link would push a `/weight-freeze-v2` URL that only
 * exists behind PrivateRoute — a blank screen with no way back for a developer
 * with no session. Keeping the hooks in a wrapper is what lets the page stay
 * uncontrolled, and routable, at the same time. Do not move them inward.
 *
 * `useParams` is not an option either: PrivateRoute registers each menu item's
 * pathname literally, so none of these paths can ever be a `:param` pattern.
 */
function RoutedWeightFreezeV2() {
  const {pathname} = useLocation()
  const routerNavigate = useNavigate()

  const handleModuleChange = React.useCallback(
    (id) => routerNavigate(pathForModule(id)),
    [routerNavigate]
  )

  // `live`: the real route talks to the backend for the two released sections (T19). The dev
  // harness mounts WeightFreezeV2 directly, without it, and stays on the frozen dataset.
  return (
    <WeightFreezeV2
      moduleId={moduleForPath(pathname).id}
      onModuleChange={handleModuleChange}
      live
    />
  )
}

// Also exported RAW (un-withLayout'd, and un-routed) for the dev-only harness at
// src/app/components/weight/__harness__/WeightHarness.js, which renders these
// pages without the app shell so layout can be checked without a login.
// withLayout pulls in Sidebar/TopBar and the authenticated merchant context.
export {WeightFreezeV2}

export default withLayout(RoutedWeightFreezeV2)
